<template>
    <div :class="meta.inner ? 'card-inner' : 'ai-card flex flex-col flex-grow h-0 overflow-hidden'">
        <div class="ai-toolbar">
            <div class="ai-card-toolbar-container sticky top-0 z-10">
                <dx-toolbar v-if="props.useDefaultButtons">
                    <dx-item
                        v-for="(item, index) in toolbarConfigAfter"
                        :key="index"
                        location="after"
                        :name="item.component.name"
                    >
                        <dx-button
                            v-if="item.widget === 'dx-button'"
                            class="ai-button-base"
                            :hint="item.component.hint"
                            :disabled="refs[item.component.disabledRefName]"
                            @click="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'click';
                                    eventController(event);
                                }
                            "
                        >
                            <component :is="item.component.htmlElement" />
                        </dx-button>
                        <dx-text-box
                            v-if="item.widget === 'dx-text-box'"
                            class="ai-toolbar-texteditor"
                            :label="
                                refs[item.component.valueLabelName]
                                    ? refs[item.component.valueLabelName]
                                    : item.component.valueLabelName
                            "
                            @enter-key="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'enter-key';
                                    eventController(event);
                                }
                            "
                            @key-up="
                                event => {
                                    event['item'] = item;
                                    event['eventName'] = 'key-up';
                                    eventController(event);
                                }
                            "
                            v-model:value="refs[item.component.valueRefName.name]"
                            :width="item.component.width"
                        >
                            <DxTextBoxButton
                                v-for="(inputButton, indexButton) in item.component.inputButtons"
                                :key="indexButton"
                                :name="inputButton.name"
                                :options="inputButton.options"
                                location="after"
                            />
                        </dx-text-box>
                        <DxDropDownButton
                            v-if="item.widget === 'dx-drop-down-button'"
                            :split-button="true"
                            :icon="item.component.htmlElement"
                            hint="Экспортировать"
                            :items="item.component.items"
                            :dropDownOptions="item.component.dropDownOptions"
                        >
                            <dx-item v-for="(item, index) in item.component.items" :key="index">
                                <div
                                    class="ai-dx-drop-down-button-item"
                                    @click="
                                        event => {
                                            event['item'] = item;
                                            event['eventName'] = 'click';
                                            eventController(event);
                                        }
                                    "
                                >
                                    <component
                                        class="ai-dx-drop-down-button-item-svg"
                                        :is="item.component.htmlElement"
                                    />
                                    <div>
                                        {{
                                            typeof item.component.text === 'function'
                                                ? item.component.text()
                                                : item.component.text
                                        }}
                                    </div>
                                </div>
                            </dx-item>
                        </DxDropDownButton>
                    </dx-item>
                </dx-toolbar>
                <dx-toolbar v-else :items="toolbarItems" />
            </div>
        </div>
        <dx-scroll-view class="ai-card-scroll-panel flex-grow overflow-auto">
            <card-group
                v-for="(item, index) in meta.groups"
                :key="index"
                :label="item.label"
                :icon="item.icon"
                :collapsed="item.collapsed"
                :components="getComponents(item)"
                :components-path="componentsPath"
                :callbacks="{
                    userDataChanged,
                    notifyErrorMessage,
                    dotsClick,
                }"
                :server-data="serverData"
            />
        </dx-scroll-view>
    </div>
</template>

<script>
export default {
    name: 'cardForm',
};
</script>

<script setup>
import { DxScrollView } from 'devextreme-vue';
import DxToolbar, { DxItem } from 'devextreme-vue/toolbar';
import { DxTextBox, DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';
import { DxButton } from 'devextreme-vue';
import DxDropDownButton from 'devextreme-vue/drop-down-button';
import { defineProps, onBeforeMount, provide, ref } from 'vue';
import CardGroup from '@/components/card-component/card-group.vue';
import AiCustomConfirmDialog from '@/utils/ai-custom-confirm-dialog';
import { useStore } from 'vuex';
import { deleteUserDataChanges, getUserDataChanges, saveUserDataChanges } from '@/service/userdata-service';
import { onBeforeRouteUpdate } from 'vue-router';

import UiNotify from 'devextreme/ui/notify';
import { custom } from 'devextreme/ui/dialog';

const props = defineProps({
    // метаданные для карточки
    meta: {
        type: Object,
        default: () => {},
    },
    // путь до всех компонентов оберток, надо указать от view и до последней директории
    componentsPath: {
        type: String,
        default: 'components',
    },
    // расширение тулбара кастомными элементами
    toolbarItems: {
        type: Array,
        default: () => [],
    },
    // вкл/выкл стандартных кнопок (пока две: обновить и сохранить)
    useDefaultButtons: {
        type: Boolean,
        default: true,
    },
    // использовать когда стандартное поведение надо переопределить
    defaultHandlers: {
        type: Object,
        default: () => ({ refresh: null, commit: null }),
    },
    // использовать для подключения бэка к стандартному поведению
    callbacks: {
        type: Object,
        default: () => ({
            refreshData: null,
            saveData: null,
            isDirty: null,
            sendUserData: null,
            getUserData: null,
            dotsClick: null,
        }),
    },
    toolbarUserItems: {
        type: Array,
        default: () => [],
    },
    toRemoveToolbarItemsNames: {
        type: Array,
        default: () => ['choice', 'edit'],
    },
});

const dotsClick = meta => {
    let newMeta = meta;
    if (props.callbacks.dotsClick) {
        newMeta = props.callbacks.dotsClick(meta);
    }
    console.log('card-form dotsClick result', JSON.stringify(newMeta));
    return newMeta;
};

const refs = ref({
    isSaveButtonDisabled: true,
});
const vIsCurrentSettingsUser = ref(true);
onBeforeMount(() => {
    // toolbar
    for (const item of toolbarConfigInitAfter) {
        if (!props.toRemoveToolbarItemsNames.includes(item.component.name)) {
            toolbarConfigAfter.push({ ...item });
        }
    }
    const compare = (a, b) => {
        if (a.index < b.index) {
            return -1;
        }
        if (a.index > b.index) {
            return 1;
        }
        return 0;
    };
    const toolbarUserItems = [...props.toolbarUserItems].sort(compare);

    for (const item of toolbarUserItems) {
        if (item.component.valueRefName) {
            refs.value[item.component.valueRefName.name] = item.component.valueRefName.value;
        }
        toolbarConfigAfter.splice(item.index, 0, item);
    }
});

let toolbarConfigAfter = [];
const toolbarConfigInitAfter = [
    {
        widget: 'dx-button',
        component: {
            name: 'refresh',
            hint: 'Обновить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-refresh-form',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'copy',
            disabledRefName: 'isCopyButtonDisabled',
            hint: 'Создать копию сфокусированной строки',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-copy-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'edit',
            disabledRefName: 'isEditButtonDisabled',
            hint: 'Редактировать',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-edit-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'delete',
            disabledRefName: 'isDeleteButtonDisabled',
            hint: 'Удалить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-delete-base',
        },
    },
    {
        widget: 'dx-button',
        component: {
            name: 'save',
            disabledRefName: 'isSaveButtonDisabled',
            hint: 'Сохранить',
            emits: [{ name: 'click' }],
            htmlElement: 'ai-save-base',
        },
    },
    {
        widget: 'dx-drop-down-button',
        component: {
            name: 'settings',
            htmlElement: `<svg width="79" height="79" viewBox="0 0 79 79" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M45.8581 4.08836C42.7691 -0.326979 36.2307 -0.326979 33.1418 4.08836L31.724 6.11496C29.8526 8.78982 26.5073 10.0074 23.3544 9.16124L20.9656 8.52012C15.7612 7.12333 10.7525 11.3261 11.2243 16.694L11.4409 19.1579C11.7267 22.4098 9.94669 25.4929 6.9875 26.8713L4.74549 27.9157C-0.139159 30.191 -1.27454 36.6301 2.53732 40.4388L4.28693 42.187C6.59621 44.4944 7.2144 48.0004 5.83357 50.9584L4.78739 53.1996C2.50808 58.0824 5.77728 63.7448 11.1456 64.2123L13.6096 64.4268C16.8617 64.71 19.5889 66.9984 20.4325 70.152L21.0717 72.5413C22.4642 77.7468 28.6083 79.9831 33.0211 76.8905L35.0466 75.471C37.7199 73.5975 41.28 73.5975 43.9533 75.471L45.9788 76.8905C50.3916 79.9831 56.5357 77.7468 57.9282 72.5413L58.5674 70.152C59.411 66.9984 62.1382 64.71 65.3903 64.4268L67.8543 64.2123C73.2226 63.7448 76.4918 58.0824 74.2125 53.1996L73.1663 50.9584C71.7855 48.0004 72.4037 44.4944 74.713 42.187L76.4626 40.4388C80.2744 36.6301 79.139 30.191 74.2544 27.9157L72.0124 26.8713C69.0532 25.4929 67.2732 22.4098 67.559 19.1579L67.7756 16.694C68.2474 11.3261 63.2387 7.12333 58.0343 8.52012L55.6455 9.16124C52.4926 10.0074 49.1473 8.78982 47.2759 6.11496L45.8581 4.08836ZM39.5 54.9611C47.4867 54.9611 53.9612 48.4866 53.9612 40.4999C53.9612 32.5132 47.4867 26.0386 39.5 26.0386C31.5133 26.0386 25.0388 32.5132 25.0388 40.4999C25.0388 48.4866 31.5133 54.9611 39.5 54.9611Z" fill="#286EB6"/>
</svg>`,
            items: [
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-default-settings',
                        text: () => {
                            return vIsCurrentSettingsUser.value ? `Заводские настройки` : `●  Заводские настройки`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'apply-user-settings',
                        text: () => {
                            return !vIsCurrentSettingsUser.value
                                ? `Настройки пользователя`
                                : `●  Настройки пользователя`;
                        },
                    },
                },
                {
                    widget: 'dx-drop-down-item',
                    component: {
                        name: 'clear-user-settings',
                        text: 'Сброс настроек пользователя',
                    },
                },
            ],
            dropDownOptions: { width: 350 },
        },
    },
];

// function dotsClick(meta) {
//     console.log('cardForm dotsClick meta:', JSON.stringify(meta));
//     return {};
// }
const eventController = async e => {
    if (e.item.widget === 'dx-button') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
    if (e.item.widget === 'dx-drop-down-item') {
        if (e.eventName === 'click') {
            await toolbarButtonClick(e);
        }
    }
};
const emit = defineEmits(['toolbarButtonClick', 'isDirty']);
const toolbarButtonClick = async e => {
    switch (e.item.component.name) {
        case 'refresh':
            await onRefreshDataButtonClick(e);
            break;
        case 'save':
            await onSaveDataButtonClick(e);
            break;
        default:
            break;
    }
    emit('toolbarButtonClick', e);
};

const serverData = ref({});
const userData = ref({});
const store = useStore();
const validationResult = ref([]);
const errMessageText = ref('');
const getControlValue = (fieldNameText, fieldNameId) => {
    let resultFieldNameText;
    if (fieldNameText) {
        resultFieldNameText = userData.value[fieldNameText];
    }
    let resultFieldNameId;
    if (fieldNameId) {
        resultFieldNameId = userData.value[fieldNameId];
    }
    return { fieldNameText: resultFieldNameText, fieldNameId: resultFieldNameId };
};
// const isInvalidForm = computed(() => {
//     return validationResult.value.length > 0 || errMessageText.value;
// });

provide('serverData', serverData);
provide('userData', userData);
provide('validationResult', validationResult);

// const saveButtonOptions = ref({
//     icon: 'save',
//     hint: 'Сохранить',
//     onClick: () => onSaveDataButtonClick(),
//     disabled: true,
// });

const onErrorPopupShow = async msg => {
    const myDialog = custom({
        title: `Ошибка сохранения карточки`,
        messageHtml: msg,
        width: 500,
    });

    msg && (await myDialog.show());
};
const onSaveDataButtonClick = async () => {
    const response = await props.callbacks.saveData({ ...userData.value });
    if (response.success) {
        errMessageText.value = '';
        validationResult.value = [];
        await clearUserData();
        await refreshData();
        return true;
    }
    if (response.errors) {
        const message = response.errors.message;
        errMessageText.value = message;
        onErrorPopupShow(errMessageText.value);
        response.errors.items && validateForm(response.errors.items);
    }
    return false;
};

const validateForm = errors => {
    errors.forEach(el => {
        errMessageText.value += ` ${el.message}</br>`;
    });
    validationResult.value = errors;
};

const notifyErrorMessage = message => {
    UiNotify({
        message: message,
        type: 'error',
        closeOnClick: true, // This option allows the notification to be closed when clicked
    });
};

onBeforeMount(async () => {
    await refreshData();
});

async function userDataChanged() {
    await sendUserData();
    refs.value['isSaveButtonDisabled'] = !isDirtyController();
    //saveButtonOptions.value = { ...saveButtonOptions.value, disabled: !isDirtyController() };
}

function isDirtyController() {
    let resultIsDirty = true;
    if (props.callbacks.isDirty === null) {
        resultIsDirty = isDirtyInternal(props.meta, serverData.value, userData.value);
    } else {
        resultIsDirty = props.callbacks.isDirty(props.meta, serverData.value, userData.value);
    }
    emit('isDirty', { value: resultIsDirty, userData: userData.value });
    return resultIsDirty;
}

const isDirtyInternal = (meta, serverData, userData) => {
    // Перечисление ключей объекта serverData
    const serverDataKeys = Object.keys(serverData);
    for (const key of serverDataKeys) {
        if (JSON.stringify(normalizeValue(serverData[key])) !== JSON.stringify(normalizeValue(userData[key]))) {
            return true;
        }
    }
    return false;
};

const normalizeValue = value => {
    if (Array.isArray(value)) {
        // Сортировка массива по значениям
        return value.sort();
    }
    if (value && typeof value === 'object') {
        // Сортировка объекта по ключам
        return Object.fromEntries(
            Object.entries(value).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)) // сортировка по ключам
        );
    }
    // Для остальных типов приводим в строку
    return String(value);
};

const refreshData = async () => {
    const refreshDataResult =
        props.callbacks.refreshData &&
        (await props.callbacks.refreshData(store.state.userDataDetails.focusedInstanceId));
    const userDataDB = await getUserData();
    if (userDataDB.length > 0) {
        userData.value = userDataDB[0];
    } else {
        if (refreshDataResult && refreshDataResult.data) {
            userData.value = JSON.parse(JSON.stringify(refreshDataResult.data));
        }
    }

    //как вариант, еще можно брать из userData.data
    await store.dispatch('addCardData', userData.value);
    if (refreshDataResult && refreshDataResult.result) {
        serverData.value = refreshDataResult.data;
        await store.dispatch('setUserDataRawData', serverData.value);
    }

    // console.log('cardform:refreshData', userData.value);
    // console.log('cardform:refreshData', serverData.value);

    refs.value['isSaveButtonDisabled'] = !isDirtyController();
    //saveButtonOptions.value = { ...saveButtonOptions.value, disabled: !isDirtyController() };
};

const getUserData = async () => {
    const args = {
        viewId: store.state.userDataDetails.focusedViewId,
        instanceId: store.state.userDataDetails.focusedInstanceId,
    };
    if (props.callbacks.getUserData) {
        return await props.callbacks.getUserData(args);
    }
    return await getUserDataChanges(args);
};

const sendUserData = async () => {
    const args = {
        viewId: store.state.userDataDetails.focusedViewId,
        instanceId: store.state.userDataDetails.focusedInstanceId,
        changes: [userData.value],
    };
    if (props.callbacks.sendUserData) {
        return await props.callbacks.sendUserData(args);
    }
    const result = await saveUserDataChanges(args);
    return result;
};

const clearUserData = async () => {
    const args = {
        viewId: store.state.userDataDetails.focusedViewId,
        instanceId: store.state.userDataDetails.focusedInstanceId,
        changes: [],
    };
    const result = await deleteUserDataChanges(args);
    return result;
};

const onRefreshDataButtonClick = async () => {
    if (isDirtyController()) {
        const result = await AiCustomConfirmDialog('У вас есть несохраненные изменения, сохранить изменения?');
        if (result === 1) {
            await onSaveDataButtonClick();
            await clearUserData();
            await refreshData();
        }
        if (result === 0) {
            errMessageText.value = '';
            validationResult.value = [];
            await clearUserData();
            await refreshData();
        }
    } else {
        errMessageText.value = '';
        validationResult.value = [];
        await clearUserData();
        await refreshData();
    }
};
const getComponents = group => {
    return props.meta?.components.filter(item => item.groupId === group.id);
};

onBeforeRouteUpdate(async () => {
    await refreshData();
});
defineExpose({
    onRefreshDataButtonClick,
    onSaveDataButtonClick,
    isDirtyController,
    clearUserData,
    sendUserData,
    userData,
    serverData,
    getControlValue,
});
</script>

<style lang="scss" scoped>
$focusedField: #ABECB4;
$changedField: #81D68C;
$changedHoverField: #59c968;

.card-inner {
    padding-right: 15px;
}

:deep(.dx-field) {
    display: flex;
    flex-direction: column;
}

:deep(.dx-field-label) {
    display: flex;
    flex-grow: 1;
    align-self: start;
    font-weight: 400;
    font-size: 12px;
    min-height: 1rem;
    width: 100%;
    padding-bottom: 3px;
}

:deep(.dx-field-value) {
    width: 100% !important;
}

:deep(.dx-field-value.ai-field-modified) {
    border-radius: 5px;
}

:deep(.dx-field-value:not(.ai-field-modified) .dx-textbox.dx-editor-filled.dx-state-hover:not(.dx-state-readonly):not(.dx-state-focused)) {
    background-color: #AED6FF;
}

:deep(.dx-textbox.dx-state-focused:not(.dx-state-readonly)) {
    background-color: $focusedField;
}

:deep(.dx-numberbox.dx-state-focused:not(.dx-state-readonly)) {
    background-color: $focusedField;
}

:deep(.ai-field-modified .dx-textbox.dx-texteditor.dx-editor-filled:not(.dx-state-hover):not(.dx-state-focused):not(.dx-state-readonly)) {
    background-color: $changedField;
}

:deep(.ai-field-modified .dx-numberbox.dx-texteditor.dx-editor-filled:not(.dx-state-hover):not(.dx-state-focused):not(.dx-state-readonly)) {
    background-color: $changedField;
}

:deep(.ai-field-modified .dx-radiogroup:not(.dx-state-hover):not(.dx-state-readonly)) {
    background-color: $changedField;
}

:deep(.ai-field-modified .dx-textbox.dx-texteditor.class-name:not(.dx-state-hover)) {
    background-color: $changedField;
}

:deep(.ai-field-modified .dx-textbox.dx-texteditor.dx-editor-filled.dx-state-hover:not(.dx-state-focused)) {
    background-color: $changedHoverField;
}

:deep(.ai-field-modified .dx-numberbox.dx-texteditor.dx-editor-filled.dx-state-hover:not(.dx-state-focused)) {
    background-color: $changedHoverField;
}

:deep(.dx-radiobutton.dx-state-focused .dx-radiobutton-icon-checked::after) {
    transform: scale(0.9);
    background: radial-gradient(circle,
        rgba(255, 255, 255, 0) 0%,
        rgba(255, 255, 255, 0.4) 40%,
        rgba(40, 110, 182, 0.5) 100%
    );
}

:deep(.dx-widget.dx-button.dx-button-mode-contained.dx-button-normal.dx-button-has-icon) {
    background-color: transparent !important;
}

:deep(.dx-texteditor-input-container.dx-tag-container) {
    width: 100%;
    padding: 4px 12px;
}

:deep(.dx-texteditor-input-container.dx-tag-container input) {
    display: none;
}

:deep(.dx-tag-content) {
    background-color: #F6FAFE !important;
    padding: 5px 20px 5px 8px;

    span {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
}

:deep(.dx-field-left) {
    flex-direction: row-reverse;
    justify-content: flex-start;

    padding-top: 20px;
}

:deep(.dx-field-label-left) {
    display: flex;
    justify-content: flex-start;
    align-self: center;
}

:deep(.dx-field-value-left) {
    max-width: 28px;
    display: flex;
    justify-content: flex-start;
}

:deep(.dx-texteditor.dx-editor-filled) {
    border-radius: 5px;
    min-height: 32px;
}

:deep(.dx-texteditor-container) {
    border-radius: 5px;
    min-height: 32px;
}

:deep(.dx-texteditor-input) {
    font-size: 12px;
}

:deep(.ai-field-modified:not(:has(.dx-state-readonly)):not(:has(.dx-state-focused)):not(:has(.dx-state-hover))) {
    background-color: $changedField;

    .dx-texteditor-container {
        background-color: transparent !important;
    }
}

:deep(.ai-field-required) {
    background-color: #ffc0cbb5;
}

:deep(.ai-dx-invalid) {
    cursor: pointer;
    position: absolute;
    top: 65%;
    right: 15px;
    margin-top: -11px;
    width: 22px;
    height: 22px;
    text-align: center;
    line-height: 23px;
    font-size: 14px;
    font-weight: 500;
    background-color: #f44336;
    color: #fff;
    content: '!';
    border-radius: 50%;
}

:deep(.ai-dx-invalid::after) {
    cursor: pointer;
    position: absolute;
    top: 50%;
    right: 40%;
    margin-top: -11px;
    text-align: center;
    line-height: 23px;
    font-size: 14px;
    font-weight: 500;
    background-color: #ffffff00;
    color: #fff;
    content: '!';
    border-radius: 50%;
}

:deep(.dx-button.dx-button-has-icon:not(.dx-button-has-text):not(.dx-shape-standard)) {
    background-color: #f5f5f5;
}

:deep(.dx-button.dx-button-has-icon.dx-state-hover:not(.dx-button-has-text):not(.dx-shape-standard)) {
    background-color: #fff;
}

:deep(.dx-toolbar .dx-toolbar-after) {
    padding-inline-end: 15px;
}

.ai-card {
    display: flex;
    padding: 0 0 40px 30px;
    background-color: #ffffff;

    .ai-card-toolbar-container {
        margin-left: auto;
        padding: 0 30px 6px 0;
        max-width: 150px;
    }

    .ai-card-toolbar-container > .dx-toolbar {
        background-color: var(--light-blue);
        border-radius: 5px;
    }

    .ai-card-toolbar-container > .dx-toolbar > .dx-toolbar-items-container {
        height: 70px;
    }

    :deep(.ai-card-scroll-panel > .dx-scrollable-wrapper > .dx-scrollable-container) {
        padding-right: 30px;
    }
}
</style>
